import {
  formatInvitationPairingCode,
  invitationPairingCodeSchema,
} from "@msys/common";
import { Button, Stack, TextField, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useUrlSearchParams } from "../commons/hooks/useUrlSearchParams.js";
import {
  ONBOARDING_BODY_SX,
  ONBOARDING_COLORS,
  ONBOARDING_OUTLINED_BUTTON_SX,
} from "../features/organisations/onboarding/onboardingDesign.js";
import {
  useAttachInvitationToPairingMutation,
  type AttachInvitationToPairingMutation,
} from "./ContinueOnAnotherDeviceSection.generated.js";
import {
  forgetRememberedPairingCode,
  readRememberedPairingCode,
} from "./continue/rememberedPairingCode.js";

type AttachError = NonNullable<
  AttachInvitationToPairingMutation["attachInvitationToPairing"]["error"]
>;

type Props = {
  refToken: string;
};

/** Keeps letters and digits, upper-cases and caps at 6 characters, displayed as XXX-XXX. */
function normaliseTyped(value: string): string {
  const raw = value
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 6);
  return raw.length > 3 ? formatInvitationPairingCode(raw) : raw;
}

function getInitialCode(urlCode: string | null): string {
  const candidates = [urlCode, readRememberedPairingCode()];
  for (const candidate of candidates) {
    if (!candidate) continue;
    const parsed = invitationPairingCodeSchema.safeParse(candidate);
    if (parsed.success) return formatInvitationPairingCode(parsed.data);
  }
  return "";
}

export const ContinueOnAnotherDeviceSection = ({ refToken }: Props) => {
  const { t } = useTranslate(["Global"]);
  const { urlSearchParams } = useUrlSearchParams();
  const [attach, { loading }] = useAttachInvitationToPairingMutation();

  const [code, setCode] = React.useState(() =>
    getInitialCode(urlSearchParams.get("pairingCode"))
  );
  const [isDone, setIsDone] = React.useState(false);
  const [error, setError] = React.useState<AttachError | "UNKNOWN" | null>(
    null
  );

  // A link with another code opened in this tab: adopt it during render
  // (rather than in an effect) so the field never shows the stale code. An
  // absent or invalid value leaves what is in the field alone.
  const urlCode = urlSearchParams.get("pairingCode");
  const [seenUrlCode, setSeenUrlCode] = React.useState(urlCode);
  if (urlCode !== seenUrlCode) {
    setSeenUrlCode(urlCode);
    const parsedUrlCode = invitationPairingCodeSchema.safeParse(urlCode);
    if (parsedUrlCode.success) {
      setCode(formatInvitationPairingCode(parsedUrlCode.data));
      setError(null);
      setIsDone(false);
    }
  }

  const parsedCode = invitationPairingCodeSchema.safeParse(code);

  const errorMessages: Record<AttachError | "UNKNOWN", string> = {
    UNKNOWN: t("Something went wrong. Please try again.", { ns: "Global" }),
    CODE_NOT_FOUND: t(
      "This code is unknown or has expired. Please check the code on the other device.",
      { ns: "Global" }
    ),
    INVITATION_NOT_OPEN: t(
      "This invitation can no longer be continued on another device.",
      { ns: "Global" }
    ),
    TOO_MANY_ATTEMPTS: t(
      "Too many attempts. Please wait a few minutes and try again.",
      { ns: "Global" }
    ),
  };

  if (isDone) {
    return (
      <Typography sx={ONBOARDING_BODY_SX}>
        {t("Done. Please continue on the other device.", { ns: "Global" })}
      </Typography>
    );
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!parsedCode.success) return;
    setError(null);
    try {
      const { data } = await attach({
        variables: { input: { code: parsedCode.data, refToken } },
      });
      const result = data?.attachInvitationToPairing;
      if (result?.ok) {
        // The pairing is consumed: a reload or the next invitation in this
        // browser must not prefill it again.
        forgetRememberedPairingCode();
        setIsDone(true);
      } else {
        setError(result?.error ?? "UNKNOWN");
      }
    } catch {
      setError("UNKNOWN");
    }
  };

  return (
    <Stack
      component="form"
      spacing={2}
      onSubmit={handleSubmit}
      sx={{ width: "100%" }}
    >
      <Typography
        variant="h2"
        sx={{
          fontSize: 20,
          fontWeight: 500,
          lineHeight: 1.2,
          color: ONBOARDING_COLORS.heading,
          textAlign: "center",
        }}
      >
        {t("Continue on another device", { ns: "Global" })}
      </Typography>
      <Typography sx={{ ...ONBOARDING_BODY_SX, fontSize: 14 }}>
        {t("Enter the code shown on the other device.", { ns: "Global" })}
      </Typography>
      <TextField
        variant="filled"
        label={t("Code", { ns: "Global" })}
        value={code}
        onChange={event => {
          setCode(normaliseTyped(event.target.value));
        }}
        error={error !== null}
        helperText={error ? errorMessages[error] : undefined}
        slotProps={{
          htmlInput: {
            autoCapitalize: "characters",
            autoComplete: "off",
            spellCheck: false,
          },
        }}
        fullWidth
      />
      <Button
        type="submit"
        fullWidth
        variant="outlined"
        disableElevation
        disabled={!parsedCode.success || loading}
        sx={{
          ...ONBOARDING_OUTLINED_BUTTON_SX,
          // The theme's disabled outline is too faint to read as a button.
          "&.Mui-disabled": {
            borderColor: ONBOARDING_COLORS.selectorBorder,
            color: ONBOARDING_COLORS.muted,
          },
        }}
      >
        {t("Continue", { ns: "Global" })}
      </Button>
    </Stack>
  );
};
